import React, { useCallback, useEffect, useState } from "react";

import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import SaveIcon from "@mui/icons-material/Save";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";

import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Card } from "../shared/Card";
import { FormRow } from "../shared/FormLayout/FormRow";
import { Snackbar } from "../shared/Snackbar";
import { SwitchgearDetails } from "./SwitchgearDetails";

import { useAddSurveyableAssetToHvSubstation } from "../data/Assets/useAddSurveyableAssetToHvSubstation";
import { CancelButton } from "../shared/CancelButton";
import { getSubAssetType, SurveyableAsset, SurveyableAssetSubmission } from "../shared/interfaces/SurveyableAsset";
import { Panel } from "../shared/interfaces/Switchgear";
import { ProgressButton } from "../shared/ProgressButton";
import { ValidationMessage } from "../shared/ValidationMessage";
import { AssetTypeKeys } from "./models/AssetTypeKeys";

interface Props {
  substationId: number;
  existingSwitchgearNames?: string[];
  editable?: boolean;
  errorUpdatingData?: () => void;
  visible: boolean;
  onClose: () => void;
  onSuccess: (id: number | undefined, name: string) => void;
  prepopulatedName?: string;
  prepopulatedPanels?: Panel[];
  assetType?: string;
}

const useStyles = makeStyles(() =>
  createStyles({
    button: {
      textTransform: "uppercase",
      marginTop: "1px",
    },
    emptyActionsContainer: {
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
    },
  }),
);

export function CreateSwitchgearWithPanelsDialog(props: Props) {
  const { t } = useTranslation();
  const classes = useStyles();
  const formValidation = useForm();

  const { onSuccess, substationId, existingSwitchgearNames } = props;

  const [switchgear, setSwitchgear] = useState<SurveyableAsset>({
    name: props.prepopulatedName || "",
    panels: [],
    assetType: props.assetType ?? AssetTypeKeys.Switchgear,
  });

  const {
    mutate: addSurveyableAssetsToHvSubstation,
    isError: errorAddingSwitchgears,
    isLoading: addingSwitchgearsLoading,
    reset: resetAddSwitchgearsToHvSubstation,
  } = useAddSurveyableAssetToHvSubstation({
    onSuccess: onAddSwitchgearSuccess,
    onError: onAddSwitchgearError,
  });
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarText, setSnackbarText] = useState("");

  const selectedAssetType = props.assetType ?? AssetTypeKeys.Switchgear;

  function handleCloseSnackbar() {
    resetAddSwitchgearsToHvSubstation();
    setShowSnackbar(false);
  }

  useEffect(() => {
    if (!errorAddingSwitchgears) {
      setShowSnackbar(false);
    }
  }, [errorAddingSwitchgears]);

  function onAddSwitchgearSuccess() {
    onSuccess(undefined, switchgear?.name || "");
    resetAddSwitchgearsToHvSubstation();
  }

  function onAddSwitchgearError() {
    setSnackbarText(t("common.dataError", { val: t("asset.addAsset") }));
    setShowSnackbar(true);
  }

  const addSwitchgear = formValidation.handleSubmit(() => {
    switchgear.assetType = selectedAssetType;
    const surveyableAssetSubmission: SurveyableAssetSubmission = {
      hvSubstationId: substationId,
      surveyableAsset: [switchgear],
    };
    addSurveyableAssetsToHvSubstation(surveyableAssetSubmission);
  });

  const handleSwitchgearNameChange = useCallback(
    (event: any) => {
      const switchgearName = event.target.value;
      formValidation.setValue("switchgearWithPanelsDialog.switchgear_name", switchgearName, { shouldValidate: true });
      formValidation.clearErrors();
      setSwitchgear((s) => ({ ...s, name: switchgearName }));
    },
    [formValidation, setSwitchgear],
  );

  function handleSwitchGearChange(newSwitchGear: SurveyableAsset) {
    formValidation.setValue("switchgearWithPanelsDialog.panels", newSwitchGear.name, { shouldValidate: true });
    formValidation.clearErrors();
    setSwitchgear(newSwitchGear);
  }

  function handleAddPrepopulatedPanels() {
    setSwitchgear((s) => ({ ...s, panels: props.prepopulatedPanels || [] }));
  }

  const header = (
    <Typography variant="h5" component="p">
      {t("common.addEntity", { entity: `$t(assetTypes.${selectedAssetType})` })}
    </Typography>
  );

  const footer = (
    <React.Fragment>
      <ProgressButton
        startIcon={<SaveIcon />}
        onClick={addSwitchgear}
        disabled={addingSwitchgearsLoading}
        loading={addingSwitchgearsLoading}
      >
        {" "}
        {t("common.save")}{" "}
      </ProgressButton>
      <CancelButton onClick={props.onClose} className="m1-1" />
    </React.Fragment>
  );

  return props.visible ? (
    <>
      <FormProvider {...formValidation}>
        <Dialog visible={props.visible} header={header} footer={footer} onHide={props.onClose}>
          <Card title={t("asset.assetDetails")} className="mb-2">
            <FormRow
              label={t("common.name", { entity: `$t(assetTypes.${selectedAssetType})` })}
              value={
                <div>
                  <InputText
                    className={
                      formValidation.formState.errors?.switchgearWithPanelsDialog?.switchgear_name
                        ? "p-invalid"
                        : undefined
                    }
                    value={switchgear?.name}
                    maxLength={50}
                    placeholder={t("common.name", {
                      entity: t(`assetTypes.${selectedAssetType}`),
                    })}
                    {...formValidation.register("switchgearWithPanelsDialog.switchgear_name", {
                      value: switchgear?.name,
                      validate: {
                        notEmpty: (value) => value && value !== "",
                        notDuplicate: (value) =>
                          !existingSwitchgearNames?.some((name) => name.toLowerCase() === value.toLowerCase()),
                      },
                    })}
                    onChange={handleSwitchgearNameChange}
                  />

                  {formValidation.formState.errors?.switchgearWithPanelsDialog?.switchgear_name &&
                    formValidation.formState.errors.switchgearWithPanelsDialog.switchgear_name.type === "notEmpty" && (
                      <ValidationMessage invalidMessage={t("asset.emptyAssetName")} validationType="error" showIcon />
                    )}
                  {formValidation.formState.errors?.switchgearWithPanelsDialog?.switchgear_name &&
                    formValidation.formState.errors.switchgearWithPanelsDialog.switchgear_name.type ===
                      "notDuplicate" && (
                      <ValidationMessage invalidMessage={t("asset.assetDuplication")} validationType="error" showIcon />
                    )}
                </div>
              }
            />
            <SwitchgearDetails
              switchgear={switchgear}
              editable
              onEdit={handleSwitchGearChange}
              emptyActions={
                switchgear?.panels?.length === 0 && props.prepopulatedPanels && props.prepopulatedPanels.length > 0 ? (
                  <div className={classes.emptyActionsContainer}>
                    <Button
                      size="small"
                      color="primary"
                      className={classes.button}
                      onClick={handleAddPrepopulatedPanels}
                      style={{ border: "0px solid red" }}
                    >
                      {t("panels.addPanelsFromResult", { val: getSubAssetType(selectedAssetType) })}
                    </Button>
                    <div>{"or use the button below to add manually."}</div>
                  </div>
                ) : undefined
              }
            />
          </Card>
        </Dialog>
        <Snackbar open={showSnackbar} onClose={handleCloseSnackbar}>
          <Alert severity="error" variant="filled">
            {snackbarText}
          </Alert>
        </Snackbar>
      </FormProvider>
    </>
  ) : null;
}
